import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useAuth, API } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  Users, Activity, TrendingUp, TrendingDown,
  Target, AlertTriangle, Droplet, CheckCircle, ShoppingCart
} from "lucide-react";
import { toast } from "sonner";

const PartnerOverview = ({ onNavigateToPatients, onNavigateToPhysicians }) => {
  const { token } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);

  const goToPatients = (filter) => {
    if (onNavigateToPatients) {
      onNavigateToPatients(filter);
    } else {
      navigate(`/partner/patients?filter=${filter}`);
    }
  };
  
  const goToPhysicians = (filter) => {
    if (onNavigateToPhysicians) {
      onNavigateToPhysicians(filter);
    } else {
      navigate(`/partner/physicians?filter=${filter}`);
    }
  };

  useEffect(() => {
    fetchOverview();
  }, [token]);

  const fetchOverview = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/partner-admin/overview`, {
        headers: { Authorization: `Bearer ${token}` },
        
      });
      
      if (response.ok) {
        const data = await response.json();
        setOverview(data);
      } else {
        toast.error("Failed to load overview");
      }
    } catch (error) {
      console.error("Fetch overview error:", error);
      toast.error("Failed to load overview");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  const totalPatients = overview?.total_patients || 0;
  const activePatients = overview?.active_patients || 0;
  const highCompliance = overview?.high_compliance || 0;
  const moderateCompliance = overview?.moderate_compliance || 0;
  const lowCompliance = overview?.low_compliance || 0;
  const avgHba1c = overview?.average_hba1c;
  const partner = overview?.partner;

  // Calculate percentages
  const highPct = totalPatients > 0 ? Math.round((highCompliance / totalPatients) * 100) : 0;
  const moderatePct = totalPatients > 0 ? Math.round((moderateCompliance / totalPatients) * 100) : 0;
  const lowPct = totalPatients > 0 ? Math.round((lowCompliance / totalPatients) * 100) : 0;
  const activePct = totalPatients > 0 ? Math.round((activePatients / totalPatients) * 100) : 0;

  return (
    <div className="space-y-6">
      {/* Partner Info Banner */}
      {partner && (
        <Card className="p-6 rounded-2xl border-green-200 bg-gradient-to-r from-green-50 to-green-100/50">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-secondary">{partner.organization_name}</h1>
              <p className="text-muted-foreground capitalize">{partner.partner_type}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">{t('partner.license_expiry')}</p>
              <p className="font-medium text-secondary">
                {new Date(partner.expiration_date).toLocaleDateString()}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Main Stats - Clickable */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        <Card 
          className="p-3 sm:p-5 rounded-2xl border-orange-100 cursor-pointer transition-all hover:shadow-md"
          onClick={() => goToPatients("all")}
          data-testid="stat-total-patients"
        >
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-green-100 flex items-center justify-center shrink-0">
              <Users className="w-5 h-5 sm:w-7 sm:h-7 text-green-600" />
            </div>
            <div>
              <p className="text-xl sm:text-3xl font-bold text-secondary">{totalPatients}</p>
              <p className="text-xs sm:text-sm text-muted-foreground">{t('partner.total_patients')}</p>
            </div>
          </div>
        </Card>

        <Card 
          className="p-3 sm:p-5 rounded-2xl border-orange-100 cursor-pointer transition-all hover:shadow-md"
          onClick={() => goToPatients("active")}
          data-testid="stat-active-patients"
        >
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-blue-100 flex items-center justify-center shrink-0">
              <Activity className="w-5 h-5 sm:w-7 sm:h-7 text-blue-600" />
            </div>
            <div>
              <p className="text-xl sm:text-3xl font-bold text-secondary">{activePatients}</p>
              <p className="text-xs sm:text-sm text-muted-foreground">{t('partner.active_7_days')}</p>
              <p className="text-[10px] sm:text-xs text-blue-600">{activePct}% {t('partner.engagement')}</p>
            </div>
          </div>
        </Card>

        <Card 
          className="p-3 sm:p-5 rounded-2xl border-orange-100 cursor-pointer transition-all hover:shadow-md"
          onClick={() => goToPatients("hba1c")}
          data-testid="stat-avg-hba1c"
        >
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-purple-100 flex items-center justify-center shrink-0">
              <Droplet className="w-5 h-5 sm:w-7 sm:h-7 text-purple-600" />
            </div>
            <div>
              <p className="text-xl sm:text-3xl font-bold text-secondary">
                {avgHba1c ? `${avgHba1c}%` : "N/A"}
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground">{t('partner.avg_est_hba1c')}</p>
            </div>
          </div>
        </Card>

        <Card 
          className="p-3 sm:p-5 rounded-2xl border-orange-100 cursor-pointer transition-all hover:shadow-md"
          onClick={() => goToPatients("good")}
          data-testid="stat-high-compliance"
        >
          <div className="flex items-center gap-2 sm:gap-4">
            <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-success/10 flex items-center justify-center shrink-0">
              <CheckCircle className="w-5 h-5 sm:w-7 sm:h-7 text-success" />
            </div>
            <div>
              <p className="text-xl sm:text-3xl font-bold text-success">{highCompliance}</p>
              <p className="text-xs sm:text-sm text-muted-foreground">{t('partner.high_compliance')}</p>
              <p className="text-[10px] sm:text-xs text-success">{highPct}% {t('partner.of_patients')}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Compliance Breakdown - Cards clickable */}
      <div className="grid md:grid-cols-2 gap-4 sm:gap-6">
        <Card className="p-4 sm:p-6 rounded-2xl border-orange-100">
          <h3 className="font-bold text-secondary mb-4">{t('partner.compliance_distribution')}</h3>
          
          {/* Visual Bar */}
          <div className="h-8 rounded-full overflow-hidden flex mb-4">
            <div className="bg-success transition-all" style={{ width: `${highPct}%` }} />
            <div className="bg-warning transition-all" style={{ width: `${moderatePct}%` }} />
            <div className="bg-destructive transition-all" style={{ width: `${lowPct}%` }} />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div 
              className="text-center p-3 bg-success/10 rounded-xl cursor-pointer hover:bg-success/20 transition-colors"
              onClick={() => goToPatients("good")}
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-success" />
                <span className="text-2xl font-bold text-success">{highCompliance}</span>
              </div>
              <p className="text-xs text-muted-foreground">{t('partner.good_compliance')}</p>
            </div>
            <div 
              className="text-center p-3 bg-warning/10 rounded-xl cursor-pointer hover:bg-warning/20 transition-colors"
              onClick={() => goToPatients("moderate")}
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <Activity className="w-4 h-4 text-warning" />
                <span className="text-2xl font-bold text-warning">{moderateCompliance}</span>
              </div>
              <p className="text-xs text-muted-foreground">{t('partner.moderate_compliance')}</p>
            </div>
            <div 
              className="text-center p-3 bg-destructive/10 rounded-xl cursor-pointer hover:bg-destructive/20 transition-colors"
              onClick={() => goToPatients("poor")}
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <TrendingDown className="w-4 h-4 text-destructive" />
                <span className="text-2xl font-bold text-destructive">{lowCompliance}</span>
              </div>
              <p className="text-xs text-muted-foreground">{t('partner.poor_compliance')}</p>
            </div>
          </div>
        </Card>

        {/* License Info */}
        {partner && (
          <Card className="p-6 rounded-2xl border-orange-100">
            <h3 className="font-bold text-secondary mb-4">{t('partner.license_management')}</h3>
            
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-green-50 rounded-xl">
                <div>
                  <p className="text-sm text-muted-foreground">{t('partner.licenses_purchased')}</p>
                  <p className="text-2xl font-bold text-secondary">{partner.license_count}</p>
                </div>
                <Target className="w-8 h-8 text-green-600" />
              </div>

              <div className="flex justify-between items-center p-4 bg-blue-50 rounded-xl">
                <div>
                  <p className="text-sm text-muted-foreground">{t('partner.active_users')}</p>
                  <p className="text-2xl font-bold text-secondary">{totalPatients}</p>
                </div>
                <Users className="w-8 h-8 text-blue-600" />
              </div>

              <div className="flex justify-between items-center p-4 bg-orange-50 rounded-xl">
                <div>
                  <p className="text-sm text-muted-foreground">{t('partner.remaining_licenses')}</p>
                  <p className="text-2xl font-bold text-primary">
                    {Math.max(0, partner.license_count - totalPatients)}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-primary" />
              </div>

              {/* Usage Progress */}
              <div>
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-muted-foreground">{t('partner.license_usage')}</span>
                  <span className="font-medium">
                    {Math.round((totalPatients / partner.license_count) * 100)}%
                  </span>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 rounded-full transition-all"
                    style={{ width: `${Math.min(100, (totalPatients / partner.license_count) * 100)}%` }}
                  />
                </div>
              </div>
              
              {/* Buy More Licenses */}
              <Button
                onClick={() => {
                  const purchaseEvent = new CustomEvent('openLicensePurchase');
                  window.dispatchEvent(purchaseEvent);
                }}
                className="w-full rounded-xl bg-primary text-white hover:bg-primary/90"
                data-testid="buy-licenses-btn"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Buy More Licenses
              </Button>
            </div>
          </Card>
        )}
      </div>

      {/* Alerts */}
      {lowCompliance > 0 && (
        <Card className="p-4 rounded-2xl border-destructive/20 bg-destructive/5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-destructive mt-0.5" />
            <div>
              <p className="font-medium text-destructive">{t('partner.patients_requiring_attention')}</p>
              <p className="text-sm text-muted-foreground mt-1">
                {t('partner.patients_attention_desc').replace('{count}', lowCompliance)}
              </p>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
};

export default PartnerOverview;
